import { authHeader } from "./auth";
import { wrapRequest, meWrapRequest } from "./nav";
import { wrapGqlRequest } from "@/services/graphqlEndPointCommonSetting/wrapGqlRequest";
import {
  CUSTOMER_PROFILE_QUERY,
  transformCustomerDataToRest,
  CHANGE_CUSTOMER_PASSWORD_MUTATION,
  UPDATE_CUSTOMER_MUTATION,
  CREATE_CUSTOMER_ADDRESS_MUTATION,
  UPDATE_CUSTOMER_ADDRESS_MUTATION,
  DELETE_CUSTOMER_ADDRESS_MUTATION,
  CUSTOMER_ORDERS_WITH_FILTER_QUERY,
  transformOrdersDataToRest,
} from "@/services/graphQl/customer.queries";
import {
  doSetPaymentMethodOnCart,
  doPlaceOrderMutation,
} from "./graphQl/user_graphql.service";

import { formatPlaceOrderResponse } from "@/services/util.service";
import {
  placeOrderWithRetry,
  setPaymentMethodWithRetry,
} from "@/services/placeOrderWithRetry";

const vueAppMagenotApiUrl = import.meta.env.VITE_MAGENTO_API_URL;
const vueAppGraphqlUrl = import.meta.env.VITE_APP_GRAPHQL_API;
const retailorId = import.meta.env.VITE_RETAILOR_ID;
const vueAppNodeslsApiUrl = import.meta.env.VITE_NODESLS_API_URL;
const assetNodeslsUrl = import.meta.env.VITE_ASSETS_NODESLS_URL;

const assetNodeslsNewUrl = import.meta.env.VITE_ASSETS_NODESLS_NEW_URL;

/*export const doValidateAddress = async (address, stateName) => {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        address: {
          country: address.countryId,
          region: stateName || address.region?.region || "",
          line1: address.streetNoOne || address.street?.[0] || "",
          line2: address.streetNoTwo || address.street?.[1] || "",
          postalCode: address.postcode,
          city: address.city,
        },
      }),
    };
    const response = await fetch(
      `${vueAppGraphqlUrl}/avalara/addressValidation`,
      requestOptions,
    );
    const data = await response.json();

    // Normalize street to always be array of 3
    const formatStreet = (line1 = "", line2 = "", line3 = "") => [
      line1,
      line2 || "",
      line3 || "",
    ];
    // Build original_address
    const original_address = {
      region: {
        region_code: null,
        region: stateName || address.region?.region || null,
        region_id: address.regionId || null,
      },
      region_id: address.regionId || null,
      country_id: address.countryId,
      street: formatStreet(
        address.streetNoOne || address.street?.[0],
        address.streetNoTwo || address.street?.[1],
      ),
      company: address.company || "",
      telephone: address.telephone || "",
      postcode: address.postcode,
      city: address.city,
      firstname: address.firstname,
      lastname: address.lastname,
    };

    if (data?.result?.error_message) {
      return [
        {
          error_message: data?.result?.error_message[0]?.details || [],
          original_address,
        },
      ];
    }
    // Build valid_address if Avalara returns it
    let valid_address = null;
    if (data?.result?.valid_address?.length) {
      const val = data.result.valid_address[0];
      valid_address = {
        region: val.region || stateName || address.region?.region,
        region_id: address.regionId || null,
        country_id: val.country,
        street: formatStreet(val.line1, val.line2, val.line3),
        telephone: address.telephone || "",
        company: address.company || "",
        postcode: val.postalCode,
        city: val.city,
        firstname: address.firstname,
        lastname: address.lastname,
      };
    }
    return [
      {
        valid_address,
        original_address,
      },
    ];
  } catch (error) {
    // Return error as null valid_address, but original_address populated
    const original_address = {
      region: {
        region_code: null,
        region: stateName || address.region?.region || null,
        region_id: address.regionId || null,
      },
      region_id: address.regionId || null,
      country_id: address.countryId,
      street: [address.streetNoOne || address.street?.[0] || ""],
      telephone: address.telephone || "",
      company: address.company || "",
      postcode: address.postcode,
      city: address.city,
      firstname: address.firstname,
      lastname: address.lastname,
    };
    // If fetch fails, return structured error
    return [
      {
        error_message: error.message,
        original_address,
      },
    ];
  }
};*/

export const doValidateAddress = async (address, stateName) => {
  // helper: always return 3 street lines
  const formatStreet = (line1 = "", line2 = "", line3 = "") => [
    line1,
    line2 || "",
    line3 || "",
  ];

  // original address builder (used in multiple places)
  const buildOriginalAddress = () => ({
    region: {
      region_code: null,
      region: stateName || null,
      region_id: address.regionId || null,
    },
    region_id: address.regionId || null,
    country_id: address.countryId,
    street: formatStreet(address.streetNoOne, address.streetNoTwo),
    telephone: address.telephone || "",
    postcode: address.postcode,
    city: address.city,
    firstname: address.firstname,
    lastname: address.lastname,
  });

  try {
    const query = `
  query AvalaraAddressValidation(
    $address: queryInput_avalaraAddressValidation_input_address_Input!
  ) {
    avalaraAddressValidation(
      input: {
        address: $address
      }
    ) {
      result {
        original_address {
          line1
          city
          region
          country
        }
        valid_address {
          addressType
          line1
          line2
          line3
          city
          region
          country
          postalCode
          latitude
          longitude
        }
        error_message {
          summary
          details
          refersTo
          severity
          source
        }
      }
      error
    }
  }
`;

    const variables = {
      address: {
        country: address.countryId,
        region: stateName,
        line1: address.streetNoOne,
        line2: address.streetNoTwo || "",
        postalCode: address.postcode,
        city: address.city,
      },
    };

    const response = await wrapGqlRequest(
      query,
      variables,
      "avalaraAddressValidation",
    );

    const original_address = buildOriginalAddress();

    // Avalara returned validation errors
    if (response?.result?.error_message?.length) {
      return [
        {
          error_message: response.result.error_message[0]?.details || [],
          original_address,
        },
      ];
    }

    // Build valid_address if Avalara returned suggestions
    let valid_address = null;
    if (response?.result?.valid_address?.length) {
      const val = response.result.valid_address[0];

      valid_address = {
        region: val.region || stateName,
        region_id: address.regionId || null,
        country_id: val.country,
        street: formatStreet(val.line1, val.line2, val.line3),
        telephone: address.telephone || "",
        postcode: val.postalCode,
        city: val.city,
        firstname: address.firstname,
        lastname: address.lastname,
      };
    }

    return [
      {
        valid_address,
        original_address,
      },
    ];
  } catch (error) {
    // Network / GraphQL failure → still return original_address
    return [
      {
        error_message: error?.message || "Address validation failed",
        original_address: buildOriginalAddress(),
      },
    ];
  }
};
export async function doFetchStates(countryId) {
  const query = `
    query GetCountry($id: String!) {
      country(id: $id) {
        id
        full_name_locale
        full_name_english
        available_regions {
          id
          code
          name
        }
      }
    }
  `;

  const variables = {
    id: countryId,
  };

  const data = await wrapGqlRequest(query, variables, "country");
  return data || null;
}

export async function doFetchCountries() {
  const query = `
    query {
        countries {
            id
            two_letter_abbreviation
            three_letter_abbreviation
            full_name_english
            full_name_locale
            available_regions {
                id
                code
                name
            }
        }
    }
  `;

  const data = await wrapGqlRequest(query, {}, "countries");
  return data || null;
}

export const doFetchBasicInfo = async () => {
  try {
    const data = await wrapGqlRequest(CUSTOMER_PROFILE_QUERY, {}, "customer");
    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(
        data.errors[0]?.message || "Failed to fetch customer data",
      );
    }
    const customer = data?.customer || data; // Try both patterns
    if (!customer) {
      console.error(
        "🔍 doFetchBasicInfo: No customer data in response, full data:",
        data,
      );
      throw new Error("No customer data returned");
    }
    return transformCustomerDataToRest(customer);
  } catch (error) {
    console.error("Error fetching customer profile:", error);
    throw error;
  }
};

export const doFetchOrderProductsListGraphQL = async (variables) => {
  try {
    const data = await wrapGqlRequest(CUSTOMER_ORDERS_WITH_FILTER_QUERY, variables)

    return transformOrdersDataToRest(data)
  } catch (error) {
    console.error("Error fetching customer orders:", error);
    throw error;
  }
};

export const doFetchChangePassword = async (currentPassword, newPassword) => {
  try {
    const variables = {
      currentPassword,
      newPassword,
    };

    const data = await wrapGqlRequest(
      CHANGE_CUSTOMER_PASSWORD_MUTATION,
      variables,
      "changeCustomerPassword",
      false,
    );

    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(data.errors[0]?.message || "Failed to change password");
    }

    const result = data?.changeCustomerPassword;
    if (!result?.email) {
      throw new Error("Password change failed - no confirmation received");
    }

    return {
      success: true,
      message: "Password changed successfully",
      email: result.email,
    };
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};
let regionCache = null;

const getRegionCodeById = async (countryId, regionId) => {
  try {
    // Use cached data if available
    if (!regionCache) {
      regionCache = await doFetchStates(countryId);
    }

    if (regionCache?.available_regions) {
      const region = regionCache.available_regions.find(
        (r) => r.id == regionId,
      );
      return region?.code || null;
    }

    return null;
  } catch (error) {
    console.error("Error fetching region code:", error);
    return null;
  }
};

export const doUpdateAddress = async (one, address) => {
  try {
    const regionCode = await getRegionCodeById(
      address.countryId,
      address.regionId,
    );

    const addressInput = {
      region: {
        region_id: parseInt(address.regionId),
        ...(regionCode && { region_code: regionCode }),
      },
      country_code: address.countryId,
      street: [address.streetNoOne, address.streetNoTwo].filter(
        (s) => s && s.trim(),
      ),
      postcode: address.postcode,
      city: address.city,
      telephone: address.telephone,
      firstname: address.firstname,
      lastname: address.lastname,
      company: address.company || "",
      default_billing: address.defaultBilling || false,
      default_shipping: address.defaultShipping || false,
    };

    let addressResult;

    if (address.id) {
      const variables = {
        id: parseInt(address.id),
        input: addressInput,
      };
      const data = await wrapGqlRequest(
        UPDATE_CUSTOMER_ADDRESS_MUTATION,
        variables,
        "updateCustomerAddress",
      );

      if (data?.errors) {
        console.error("GraphQL errors:", data.errors);
        throw new Error(data.errors[0]?.message || "Failed to update address");
      }

      addressResult = data;
    } else {
      const variables = { input: addressInput };
      const data = await wrapGqlRequest(
        CREATE_CUSTOMER_ADDRESS_MUTATION,
        variables,
        "createCustomerAddress",
      );

      if (data?.errors) {
        console.error("GraphQL errors:", data.errors);
        throw new Error(data.errors[0]?.message || "Failed to create address");
      }

      addressResult = data?.createCustomerAddress
        ? data?.createCustomerAddress
        : data;
    }

    if (!addressResult) {
      throw new Error("Address operation failed - no confirmation received");
    }

    const customerData = await wrapGqlRequest(
      CUSTOMER_PROFILE_QUERY,
      {},
      "customer",
    );

    if (customerData?.errors) {
      console.error("Error fetching updated customer:", customerData.errors);
      return { success: true, address: addressResult };
    }

    return transformCustomerDataToRest(customerData);
  } catch (error) {
    console.error("Error updating address:", error);
    throw error;
  }
};

export const doUpdateUserAddress = async (address) => {
  try {
    const regionCode = await getRegionCodeById(
      address.country_id,
      address.region_id || address.region?.regionId,
    );
    const addressInput = {
      region: {
        region_id: parseInt(address.region_id || address.region?.regionId),
        ...(regionCode && { region_code: regionCode }),
      },
      country_code: address.country_id,
      street: Array.isArray(address.street)
        ? address.street.filter((s) => s && s.trim())
        : [address.street].filter((s) => s && s.trim()),
      postcode: address.postcode,
      city: address.city,
      telephone: address.telephone,
      firstname: address.firstname,
      lastname: address.lastname,
      company: address.company || "",
      default_billing: address.default_billing || false,
      default_shipping: address.default_shipping || false,
    };
    let addressResult;

    if (address.id) {
      const variables = {
        id: parseInt(address.id),
        input: addressInput,
      };
      const data = await wrapGqlRequest(
        UPDATE_CUSTOMER_ADDRESS_MUTATION,
        variables,
        "updateCustomerAddress",
      );
      if (data?.errors) {
        console.error("GraphQL errors:", data.errors);
        throw new Error(data.errors[0]?.message || "Failed to update address");
      }

      addressResult = data;
    } else {
      const variables = { input: addressInput };
      const data = await wrapGqlRequest(
        CREATE_CUSTOMER_ADDRESS_MUTATION,
        variables,
        "createCustomerAddress",
      );

      if (data?.errors) {
        console.error("GraphQL errors:", data.errors);
        throw new Error(data.errors[0]?.message || "Failed to create address");
      }
      addressResult = data;
    }
    if (!addressResult) {
      throw new Error("Address operation failed - no confirmation received");
    }

    const customerData = await wrapGqlRequest(
      CUSTOMER_PROFILE_QUERY,
      {},
      "customer",
    );
    if (customerData?.errors) {
      console.error("Error fetching updated customer:", customerData.errors);
      return { success: true, address: addressResult };
    }
    return transformCustomerDataToRest(customerData);
  } catch (error) {
    console.error("Error updating user address:", error);
    throw error;
  }
};

export const doUpdateSubscription = async (one, customerNumber) => {
  try {
    const input = {
      firstname: one.firstname,
      lastname: one.lastname,
      is_subscribed: one.isSubscribed,
      custom_attributes: customerNumber
        ? [
          {
            attribute_code: "customer_number",
            value: customerNumber,
          },
        ]
        : [],
    };

    const variables = { input };
    const data = await wrapGqlRequest(
      UPDATE_CUSTOMER_MUTATION,
      variables,
      "updateCustomerV2",
    );

    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(
        data.errors[0]?.message || "Failed to update subscription",
      );
    }

    const result = data?.customer;
    if (!result) {
      throw new Error("Customer update failed - no confirmation received");
    }

    return transformCustomerDataToRest(result);
  } catch (error) {
    console.error("Error updating subscription:", error);
    throw error;
  }
};

export const doChangeBasicInfo = async (customer) => {
  try {
    const input = {
      firstname: customer.firstname,
      lastname: customer.lastname,
      middlename: customer.middlename,
      prefix: customer.prefix,
      suffix: customer.suffix,
      taxvat: customer.taxvat,
      gender: customer.gender,
      date_of_birth: customer.date_of_birth,
      is_subscribed: customer.is_subscribed,
      custom_attributes: customer.custom_attributes || [],
    };

    const variables = { input };
    const data = await wrapGqlRequest(
      UPDATE_CUSTOMER_MUTATION,
      variables,
      "updateCustomerV2",
    );

    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(
        data.errors[0]?.message || "Failed to update customer information",
      );
    }

    const result = data?.customer;
    if (!result) {
      throw new Error("Customer update failed - no confirmation received");
    }

    return transformCustomerDataToRest(result);
  } catch (error) {
    console.error("Error updating customer information:", error);
    throw error;
  }
};

export const doChangePassword = async (currentPassword, newPassword) => {
  try {
    const variables = {
      currentPassword,
      newPassword,
    };

    const data = await wrapGqlRequest(
      CHANGE_CUSTOMER_PASSWORD_MUTATION,
      variables,
      "changeCustomerPassword",
      false,
    );

    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(data.errors[0]?.message || "Failed to change password");
    }

    const result = data;

    if (!result?.email) {
      throw new Error("Password change failed - no confirmation received");
    }

    return {
      success: true,
      message: "Password changed successfully",
      email: result.email,
    };
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const doFetchEstimatedShippingMethods = async (userQuote, address) => {
  let streetAddress = [];
  if (!address.streetNoTwo || address.streetNoTwo.trim() === "") {
    streetAddress = [address.streetNoOne];
  } else {
    streetAddress = [address.streetNoOne, address.streetNoTwo];
  }
  const query = `
   mutation {
     estimateShippingMethods(
       input: {
         cart_id: "${userQuote}"
         address: {
           country_code: ${address.countryId}
           region: {
             region_id: ${address.regionId}
           }
           postcode: "${address.postcode}"
         }
       }
     ) {
       carrier_code
       carrier_title
       method_code
       method_title
       error_message
       amount {
         currency
         value
       }
       price_excl_tax {
         currency
         value
       }
       price_incl_tax {
         currency
         value
       }
     }
   }
 `;
  const response = await wrapGqlRequest(query, {}, "estimateShippingMethods");
  return response.map((method) => ({
    carrier_code: method.carrier_code,
    carrier_title: method.carrier_title,
    method_code: method.method_code,
    method_title: method.method_title,
    error_message: method.error_message,
    amount: method.amount?.value ?? 0,
    base_amount: method.amount?.value ?? 0,
    available: true,
    price_excl_tax: method.price_excl_tax?.value ?? 0,
    price_incl_tax: method.price_incl_tax?.value ?? 0,
  }));
};

export const doShippingInformation = async (
  userQuote,
  one,
  address,
  estimated,
  suggestedAddress,
) => {
  const streetArray = [
    suggestedAddress.streetNoOne || address.streetNoOne,
    suggestedAddress.streetNoTwo || address.streetNoTwo,
  ].filter((v) => v && v.trim() !== "");
  const shippingAddress = {
    region:
      suggestedAddress.region && suggestedAddress.region.length
        ? suggestedAddress.region[0]
        : address.regionId && address.regionId
          ? address.regionId.toString()
          : "",
    country_code: suggestedAddress.country_id || address.countryId,
    street: streetArray,
    postcode: suggestedAddress.postcode || address.postcode,
    city: suggestedAddress.city || address.city,
    firstname: address.firstname,
    lastname: address.lastname,
    telephone: address.telephone,
    save_in_address_book: false
  };
  // Set Shipping Addresses On Cart
  await wrapGqlRequest(
    `
    mutation ($input: SetShippingAddressesOnCartInput!) {
      setShippingAddressesOnCart(input: $input) {
        cart { id }
      }
    }
    `,
    {
      input: {
        cart_id: userQuote,
        shipping_addresses: [{ address: shippingAddress }],
      },
    },
    "setShippingAddressesOnCart",
  );
  // Set Shipping Methods On Cart
  await wrapGqlRequest(
    `
     mutation ($input: SetShippingMethodsOnCartInput!) {
       setShippingMethodsOnCart(input: $input) {
         cart { id }
       }
     }
     `,
    {
      input: {
        cart_id: userQuote,
        shipping_methods: [
          {
            carrier_code: estimated.carrierCode,
            method_code: estimated.methodCode,
          },
        ],
      },
    },
    "setShippingMethodsOnCart",
  );

  // change if needed data also
  return null;

  const cart = await wrapGqlRequest(
    `
     query ($cartId: String!) {
     cart(cart_id: $cartId) {
       itemsV2 {
         items {
           uid
           quantity
           prices {
             price { value currency }
             row_total { value currency }
             row_total_including_tax { value currency }
             total_item_discount { value currency }
             discounts { amount { value currency } }
           }
           product {
             name
             sku
           }
         }
       }
       prices {
         grand_total { value currency }
         subtotal_excluding_tax { value currency }
         subtotal_including_tax { value currency }
         discounts { amount { value currency }, label }
         applied_taxes { amount { value currency } }
       }
       shipping_addresses {
         firstname
         lastname
         street
         city
         postcode
         telephone
         region {
          code
          label
          region_id
         }
         country {
          code
          label
         }
         selected_shipping_method {
           carrier_code
           carrier_title
           method_code
           method_title
           amount { value currency }
         }
       }
       available_payment_methods {
         code
         title
       }
       applied_coupons {
        code
       }
       applied_store_credit {
        enabled
        applied_balance {
          currency
          value
        }
        current_balance {
          currency
          value
        }
       }
     }
   }
     `,
    { cartId: userQuote },
    "cart",
  );
  const items = cart.itemsV2.items.map((item) => ({
    item_id: item.uid,
    qty: item.quantity,
    price: item.prices.price.value,
    base_price: item.prices.price.value,
    row_total: item.prices.row_total.value,
    base_row_total: item.prices.row_total.value,
    row_total_with_discount:
      item.prices.row_total.value -
      (item.prices.total_item_discount?.value || 0),
    base_row_total_with_discount:
      item.prices.row_total.value -
      (item.prices.total_item_discount?.value || 0),
    tax_amount: item.prices.total_item_discount?.value || 0,
    base_tax_amount: item.prices.total_item_discount?.value || 0,
    price_incl_tax: item.prices.row_total_including_tax.value,
    base_price_incl_tax: item.prices.row_total_including_tax.value,
    row_total_incl_tax: item.prices.row_total_including_tax.value,
    base_row_total_incl_tax: item.prices.row_total_including_tax.value,
    discount_amount: item.prices.total_item_discount?.value || 0,
    base_discount_amount: item.prices.total_item_discount?.value || 0,
    name: item.product.name,
  }));
  const totals = {
    grand_total: cart.prices.grand_total.value,
    base_grand_total: cart.prices.grand_total.value,
    subtotal: cart.prices.subtotal_excluding_tax.value,
    base_subtotal: cart.prices.subtotal_excluding_tax.value,
    discount_amount: cart.prices.discounts?.[0]?.amount.value || 0,
    base_discount_amount: `-${(cart?.prices?.discounts || []).reduce(
      (sum, tax) => {
        return sum + parseFloat(tax.amount.value);
      },
      0,
    )}`,
    subtotal_with_discount:
      cart.prices.subtotal_excluding_tax.value -
      (cart.prices.discounts?.[0]?.amount.value || 0),
    base_subtotal_with_discount:
      cart.prices.subtotal_excluding_tax.value -
      (cart.prices.discounts?.[0]?.amount.value || 0),
    shipping_amount:
      cart.shipping_addresses?.[0]?.selected_shipping_method?.amount?.value ||
      0,
    base_shipping_amount:
      cart.shipping_addresses?.[0]?.selected_shipping_method?.amount?.value ||
      0,
    items_qty: items.reduce((sum, i) => sum + i.qty, 0),
    items,
    total_segments: [
      {
        code: "subtotal",
        title: "Subtotal",
        value: cart.prices.subtotal_excluding_tax.value,
      },
      {
        code: "shipping",
        title: `Shipping & Handling (${cart.shipping_addresses?.[0]?.selected_shipping_method?.carrier_title} - ${cart.shipping_addresses?.[0]?.selected_shipping_method?.method_title})`,
        value:
          cart.shipping_addresses?.[0]?.selected_shipping_method?.amount
            ?.value || 0,
      },
      {
        code: "grand_total",
        title: "Order Total",
        value: cart.prices.grand_total.value,
        area: "footer",
      },
      {
        code: "tax",
        value: (cart?.prices?.applied_taxes || []).reduce((sum, tax) => {
          return sum + parseFloat(tax.amount.value);
        }, 0),
      },
      {
        code: "customerbalance",
        value: cart?.applied_store_credit?.applied_balance?.value,
      },
    ],
    tax_amount: (cart?.prices?.applied_taxes || []).reduce((sum, tax) => {
      return sum + parseFloat(tax.amount.value);
    }, 0),
  };
  const totalDiscAmount =
    (cart?.prices?.discounts || []).reduce((sum, disc) => {
      return sum + parseFloat(disc.amount.value);
    }, 0) ?? 0.0;
  if (totalDiscAmount > 0) {
    totals?.total_segments?.push({
      code: "discount",
      title: cart?.prices?.discounts.map((item) => item.label).join(", "),
    });
  }
  const payment_methods = (cart.available_payment_methods || []).filter(
    (method) => method.code && method.title,
  );

  return {
    payment_methods,
    totals,
    shippingAddress: cart.shipping_addresses[0],
  };
};

export const doSetBillingInformation = async (userQuote, address) => {
  const addressData = {
    firstname: address.firstname,
    lastname: address.lastname,
    street: address.street,
    city: address.city,
    postcode: address.postcode,
    telephone: address.telephone,
    country_code: address.countryId,
    region: address.regionId,
    save_in_address_book: false,
  };
  // Set Billing Address On Cart
  return await wrapGqlRequest(
    `
     mutation ($input: SetBillingAddressOnCartInput!) {
       setBillingAddressOnCart(input: $input) {
         cart { id }
       }
     }
     `,
    {
      input: {
        cart_id: userQuote,
        billing_address: { address: addressData },
      },
    },
    "setBillingAddressOnCart",
  );
};

export const doDeleteAddress = async (addressId) => {
  try {
    const variables = { id: parseInt(addressId) };
    const data = await wrapGqlRequest(
      DELETE_CUSTOMER_ADDRESS_MUTATION,
      variables,
      "deleteCustomerAddress",
    );
    if (data?.errors) {
      console.error("GraphQL errors:", data.errors);
      throw new Error(data.errors[0]?.message || "Failed to delete address");
    }
    const result = data;
    if (!result) {
      throw new Error("Address deletion failed - no confirmation received");
    }
    return { success: true, deleted: true };
  } catch (error) {
    console.error("Error deleting address:", error);
    throw error;
  }
};

export const doProductsOrder = async (userQuote, paymentMethod) => {
  // const requestOptions = {
  //   method: "POST",
  //   headers: {
  //     "Content-type": "application/json",
  //     ...authHeader(),
  //   },
  //   body: JSON.stringify({
  //     cartId: userQuote,
  //     billingAddress: oneAddress,
  //     paymentMethod: paymentMethod,
  //     comments: "",
  //   }),
  // };
  // Retried too: the card is already charged, and until this succeeds placeOrder
  // is never attempted at all.
  const setPayment = await setPaymentMethodWithRetry({
    setPaymentMethod: () => doSetPaymentMethodOnCart(userQuote, paymentMethod),
    isSuccess: (response) =>
      Boolean(response?.cart?.selected_payment_method?.code),
    cartId: userQuote,
    useCustomerCart: true,
  });

  if (setPayment?.alreadyPlaced) {
    return { alreadyPlaced: true, code: setPayment.code };
  }

  const setPaymentMethodResponse = setPayment?.response;
  // After setting the payment method, call the placeOrder mutation
  // and return the result.
  // Check if the payment method was set successfully
  if (setPaymentMethodResponse?.cart?.selected_payment_method?.code) {
    if (setPaymentMethodResponse) {
      // Prepare the placeOrder mutation
      try {
        // Payment is already captured by this point, so placement is retried
        // rather than failed outright. setPaymentMethodOnCart stays outside the
        // retry: it persists on the quote, so re-sending it is wasted work.
        const result = await placeOrderWithRetry({
          placeOrder: () => doPlaceOrderMutation(userQuote),
          cartId: userQuote,
          useCustomerCart: true,
        });

        if (result?.orderV2) {
          return formatPlaceOrderResponse(result.orderV2);
        }
        if (result?.alreadyPlaced) {
          return { alreadyPlaced: true, code: result.code };
        }
        return { message: result?.message || "Unknown error occurred" };
      } catch (error) {
        // placeOrderWithRetry throws once all 5 attempts are exhausted (or on
        // an auth error). The card is already charged at this point, so the
        // caller must see this failure — swallowing it here silently drops the
        // Datadog report checkout_page.vue's processPayment sends on catch.
        console.error("Error in doPlaceOrderMutation:", error);
        throw error;
      }
    } else {
      throw new Error("Failed to set payment method on cart");
    }
  } else {
    // If setting payment method failed, throw an error
    throw new Error("Failed to set payment method on cart");
  }

  // return fetch(
  //   `${vueAppMagenotApiUrl}/rest/V1/carts/mine/payment-information`,
  //   requestOptions
  // );
};

export const doProductsOrderOnCC = meWrapRequest(
  (orderNumber, shippingAddress, webOrderNumber, estimatedShipping) => {
    let address2 = shippingAddress.street[1]
      ? escape(shippingAddress.street[1])
      : undefined;
    let method = "bes";
    if (estimatedShipping) {
      method = estimatedShipping.methodCode.substring(0, 3);
    }
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify({
        retailorId: retailorId,
        orderNumber: orderNumber,
        shipToFirstName: escape(shippingAddress.firstname),
        shipToLastName: escape(shippingAddress.lastname),
        shipToAddress1: escape(shippingAddress.street[0]),
        shipToAddress2: address2,
        shipToCity: shippingAddress.city,
        shipToState: shippingAddress.regionCode
          ? shippingAddress.regionCode
          : shippingAddress.region_id,
        shipToZip: shippingAddress.postcode,
        shipToCountry: shippingAddress.countryId
          ? shippingAddress.countryId
          : shippingAddress.country_code,
        shipToPhone: shippingAddress.telephone,
        shipToEmail: shippingAddress.email,
        shippingMethod: method,
        webOrderNumber: webOrderNumber,
      }),
    };

    return fetch(`${vueAppNodeslsApiUrl}/cc-submit-order`, requestOptions);
  },
);

export const doFetchProductPdfs = meWrapRequest((orderNumber, itemId) => {
  const requestOptions = {
    method: "GET",
  };
  return fetch(
    `${assetNodeslsUrl}/order-builder-2?orderId=${orderNumber}&itemId=${itemId}`,
    requestOptions,
  );
});



export const dosetFingerprintWithUserId = wrapRequest((basicData) => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
    },
  };
  return fetch(
    `${assetNodeslsNewUrl}/fingerprint-update-request?requestId=${basicData.requestId}&linkedId=${basicData.userId}`,
    requestOptions,
  );
});
export const doSetPaymentInformation = meWrapRequest(
  ({ quoteId, email, paymentMethod, billingAddress }) => {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({
        email: email,
        cartId: quoteId,
        billingAddress: billingAddress,
        paymentMethod: paymentMethod,
        comments: "",
      }),
    };
    return fetch(
      `${vueAppMagenotApiUrl}/rest/V1/carts/mine/set-payment-information`,
      requestOptions,
    );
  },
);
export const doPlaceOrder = meWrapRequest(() => {
  const requestOptions = {
    method: "PUT",
    headers: {
      "Content-type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({}),
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/V1/carts/mine/order`,
    requestOptions,
  );
});
